import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { openShedStore } from './store';

// One store per app session: every screen (Skuret, the recipe chat, the
// "Lav en opskrift" buttons) shares it, so a save anywhere shows everywhere.
let shared = null;
export function sharedShedStore() {
  if (!shared) shared = openShedStore(typeof window === 'undefined' ? globalThis : window);
  return shared;
}

const ShedCtx = createContext(null);
const listeners = new Set();

// Called after every local change; the optional account sync listens here.
export function onShedChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function ShedProvider({ owner, open = sharedShedStore, children }) {
  const [opened, setOpened] = useState(null);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let alive = true;
    open().then(result => { if (alive) setOpened(result); });
    return () => { alive = false; };
  }, [open]);
  const bump = useCallback(() => {
    setVersion(v => v + 1);
    listeners.forEach(listener => { try { listener(); } catch { /* a listener must not break saving */ } });
  }, []);
  const value = useMemo(() => (opened ? { ...opened, owner, version, bump } : null), [opened, owner, version, bump]);
  return <ShedCtx.Provider value={value}>{children}</ShedCtx.Provider>;
}

export const useShed = () => useContext(ShedCtx);

// Live list of one kind (newest first); null until the store has answered.
export function useShedList(kind) {
  const shed = useShed();
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!shed) return undefined;
    let alive = true;
    shed.store.list(shed.owner, kind).then(rows => { if (alive) setItems(rows); });
    return () => { alive = false; };
  }, [shed, kind]);
  return items;
}

export function useShedRecord(id) {
  const shed = useShed();
  const [record, setRecord] = useState(undefined);
  useEffect(() => {
    if (!shed || !id) return undefined;
    let alive = true;
    shed.store.get(shed.owner, id).then(row => { if (alive) setRecord(row); });
    return () => { alive = false; };
  }, [shed, id]);
  return record;
}

// Saving and deleting always go through here so every screen refreshes.
export function useShedActions() {
  const shed = useShed();
  const ref = useRef(shed);
  ref.current = shed;
  return useMemo(() => ({
    async save(kind, data, id) {
      const current = ref.current;
      const record = await current.store.put(current.owner, kind, data, { id });
      current.bump();
      return record;
    },
    async remove(id) {
      const current = ref.current;
      const tomb = await current.store.remove(current.owner, id);
      current.bump();
      return tomb;
    },
    async restore(record) {
      const current = ref.current;
      const back = await current.store.put(current.owner, record.kind, record.data, { id: record.id });
      current.bump();
      return back;
    },
  }), []);
}
